import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { FlagsPanel } from "@/components/admin/moderation/FlagsPanel";
import { ViolationLog } from "@/components/admin/moderation/ViolationLog";
import { toast } from "sonner";
import { ShieldAlert, ArrowLeft } from "lucide-react";

export default function AdminModeration() {
  const { user } = useAuth();
  const [flags, setFlags] = useState(null);
  const [events, setEvents] = useState(null);

  const load = useCallback(async () => {
    try {
      const [f, e] = await Promise.all([api.get("/admin/moderation/flags"), api.get("/admin/moderation/events")]);
      setFlags(f.data);
      setEvents(e.data);
    } catch {
      toast.error("Could not load moderation data");
      setFlags([]);
      setEvents([]);
    }
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const blocked = events?.length || 0;
  const strikes = events?.filter((e) => e.strike).length || 0;

  return (
    <Layout>
      <div className="max-w-5xl w-full mx-auto px-6 py-8 space-y-6" data-testid="admin-moderation-page">
        <div>
          <Link to="/admin" data-testid="moderation-back-link"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-white transition-colors mb-3">
            <ArrowLeft className="w-3 h-3" /> Back to Admin
          </Link>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-2 mb-2">
            <ShieldAlert className="w-4 h-4 text-[#FF3B30]" /> Admin · Moderation
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter">Outgoing message moderation</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
            Every inquiry a user tries to send is read by the AI against the active flags below. A match blocks
            the send, tells the user which flag fired, and logs the attempt here. You can add or edit flags
            any time and issue strikes when a blocked message shows real malice.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3" data-testid="moderation-stats">
          {[["Active flags", flags?.filter((f) => f.active).length ?? "—", "#007AFF"],
            ["Blocked messages", blocked, "#FFCC00"],
            ["Strikes issued", strikes, "#FF3B30"]].map(([label, val, color]) => (
            <div key={label} className="border border-border bg-[#0A0A0A] rounded-sm p-4">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">{label}</p>
              <p className="font-heading text-3xl font-black tracking-tighter mt-1" style={{ color }}>{val}</p>
            </div>
          ))}
        </div>

        <ViolationLog events={events} onStrike={(updated) =>
          setEvents((list) => list.map((e) => (e.id === updated.id ? updated : e)))} />

        <FlagsPanel flags={flags} onChanged={load} />
      </div>
    </Layout>
  );
}
